import { useState, useEffect, useRef } from 'react';
import { DataTable, type DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Message } from 'primereact/message';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { AllocationSuggestion, SuggestedDeposit } from '../../api';
import AllocationControls, { type AllocationControlsHandle } from './AllocationControls';
import { formatDate } from '../../utils';

interface SuggestionsTabProps {
  clientId: string;
  suggestions: AllocationSuggestion[];
  onAccept: (suggestion: AllocationSuggestion) => Promise<void>;
  onSuggestionsChange: (suggestions: AllocationSuggestion[]) => void;
  onRefresh: () => Promise<void>;
  onError: (message: string | null) => void;
  saving: boolean;
  savingLedgerEntryId: string | null;
}

export default function AllocationSuggestionsTab({
  clientId,
  suggestions,
  onAccept,
  onSuggestionsChange,
  onRefresh,
  onError,
  saving,
  savingLedgerEntryId,
}: SuggestionsTabProps) {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [editableSuggestions, setEditableSuggestions] = useState<AllocationSuggestion[]>([]);
  const allocationControlsRef = useRef<AllocationControlsHandle | null>(null);

  useEffect(() => {
    // Only initialize/reset when suggestions truly change (IDs or length differ)
    // This prevents reset when navigating between tabs
    const currentIds = editableSuggestions.map(s => s.ledger_entry_id).sort().join(',');
    const newIds = suggestions.map(s => s.ledger_entry_id).sort().join(',');

    if (currentIds !== newIds) {
      setEditableSuggestions(JSON.parse(JSON.stringify(suggestions)));
    }
  }, [suggestions, editableSuggestions]);

  const handleRemoveDeposit = (suggestion: AllocationSuggestion, depositToRemove: SuggestedDeposit) => {
    setEditableSuggestions(prevSuggestions =>
      prevSuggestions.map(s => {
        if (s.ledger_entry_id === suggestion.ledger_entry_id) {
          const newDeposits = s.suggested_deposits.filter(
            d => d.id !== depositToRemove.id
          );
          const newTotalAmount = newDeposits.reduce((sum, d) => sum + parseFloat(String(d.amount || 0)), 0);
          const newDifference = parseFloat(String(s.ledger_entry.amount || 0)) - newTotalAmount;

          return {
            ...s,
            suggested_deposits: newDeposits,
            total_amount: newTotalAmount,
            difference: newDifference,
          };
        }
        return s;
      })
    );
  };

  const rowExpansionTemplate = (originalSuggestion: AllocationSuggestion) => {
    const suggestion = editableSuggestions.find(s => s.ledger_entry_id === originalSuggestion.ledger_entry_id);

    if (!suggestion) return null;

    const depositActionsTemplate = (deposit: SuggestedDeposit) => (
      <Button
        icon="ms-out ms-out-delete"
        link
        size="small"
        severity="danger"
        onClick={() => handleRemoveDeposit(suggestion, deposit)}
        title="Remove deposit from suggestion"
      />
    );

    return (
      <div className="row-expansion-container">
        <Panel header="Suggested Deposits">
          <DataTable value={suggestion.suggested_deposits} size="small">
            <Column
              field="deposit_date"
              style={{ width: '12%' }}
              header="Date"
              body={(rowData: SuggestedDeposit) => formatDate(rowData.deposit_date)}
            />
            <Column field="customer_name" style={{ width: '25%' }} header="Customer" />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '12%' }}
              body={(rowData: SuggestedDeposit) =>
                parseFloat(String(rowData.amount || 0)).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })
              }
            />
            <Column body={depositActionsTemplate} />
          </DataTable>
        </Panel>
      </div>
    );
  };

  const ledgerEntryTemplate = (suggestion: AllocationSuggestion) => {
    const { ledger_entry } = suggestion;
    return (
      <div>
        <div>{formatDate(ledger_entry.entry_date)}</div>
        <div><i>{ledger_entry.description}</i></div>
      </div>
    );
  }

    const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '$0.00';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numeric)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numeric);
  };

  const amountBodyTemplate = (suggestion: AllocationSuggestion) => {
    // Show the amounts from the editable version if available
    const editable = editableSuggestions.find(s => s.ledger_entry_id === suggestion.ledger_entry_id) || suggestion;
    return (
          <div>
            <span>Ledger: <strong>{formatCurrency(editable.ledger_entry.amount)}</strong></span>
            <span> - </span>
            <span>Deposit(s): <strong>{formatCurrency(editable.total_amount)}</strong></span>
            <span> = </span>
            <span>Remaining: <strong>{formatCurrency(editable.difference)}</strong></span>
          </div>
    );
  };

  const handleAcceptSuggestion = async (suggestion: AllocationSuggestion) => {
    try {
      await onAccept(suggestion);
      await allocationControlsRef.current?.runAllocations();
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
    }
  };

  const actionsBodyTemplate = (suggestion: AllocationSuggestion) => (
    <Button
      icon="ms-out ms-out-check"
      link
      size="small"
      severity="success"
      onClick={() => handleAcceptSuggestion(suggestion)}
      title="Accept Allocations"
      disabled={saving && savingLedgerEntryId === suggestion.ledger_entry_id}
    />
  );

  return (
    <>
      <ConfirmDialog />
      <AllocationControls
        ref={allocationControlsRef}
        clientId={clientId}
        onSuggestionsChange={onSuggestionsChange}
        onParamsChange={() => {}} // No-op since we don't need to track params in SuggestionsTab
        onRefresh={onRefresh}
        onSwitchToSuggestions={() => {}} // No-op since we're already in SuggestionsTab
        onError={onError}
      />
      {suggestions.length > 0 ? (
        <DataTable
          value={suggestions}
          dataKey="ledger_entry_id"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          size="small"
          scrollable
          scrollHeight="calc(100vh - 200px)"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          paginatorTemplate={{
            layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
            CurrentPageReport: (options) => (
              <span className="paginator-current">
                Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> suggestions
              </span>
            )
          }}
        >
          <Column key="expander" columnKey="expander" expander style={{ width: '3%' }} />
          <Column
            key="ledgerEntry"
            columnKey="ledgerEntry"
            header="Ledger Entry"
            style={{ width: '20%' }}
            body={ledgerEntryTemplate}
          />
          <Column
            key="amounts"
            columnKey="amounts"
            header="Amounts"
            style={{ width: '40%' }}
            body={amountBodyTemplate}
          />
          <Column key="actions" columnKey="actions" body={actionsBodyTemplate} />
        </DataTable>
      ) : (
          <Message severity="info" text="No suggestions to display" />
      )}
    </>
  );
}
